import type { FC } from 'react';
import { Checkbox } from '@/components/ui/checkbox';
import { useTranslation } from '@/hooks/useTranslation';
import { groupSignals, SIGNALS_MAX } from '../lib/signalGroups';
import { channelLabel } from '../lib/signalChannels';

interface SignalGroupPickerProps {
  signals: string[];
  loading: boolean;
  machineSelected: boolean;
  checked: Set<string>;
  onToggle: (tag: string) => void;
}

export const SignalGroupPicker: FC<SignalGroupPickerProps> = ({
  signals,
  loading,
  machineSelected,
  checked,
  onToggle,
}) => {
  const { t } = useTranslation();

  if (!machineSelected) {
    return <p className="text-xs text-muted-foreground">{t('signals.panel.signalsEmptyMachine')}</p>;
  }

  if (loading) {
    return <p className="text-xs text-muted-foreground">{t('signals.panel.signalsLoading')}</p>;
  }

  const groups = groupSignals(signals);

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-foreground">{t('signals.panel.signalsLabel')}</span>
        <span className="text-xs text-muted-foreground" data-testid="signals-counter">
          {t('signals.panel.signalsCounter', { count: checked.size, max: SIGNALS_MAX })}
        </span>
      </div>
      <div className="flex max-h-72 flex-col gap-3 overflow-y-auto pr-1">
        {groups.map((group) => (
          <div key={group.key} className="flex flex-col gap-1.5">
            <span className="eyebrow">{t(`signals.panel.groups.${group.key}`)}</span>
            <div className="flex flex-col gap-1.5">
              {group.signals.map((tag) => (
                <label key={tag} className="flex items-center gap-2 text-sm text-foreground">
                  <Checkbox
                    data-testid={`signal-tag-checkbox-${tag}`}
                    checked={checked.has(tag)}
                    onCheckedChange={() => onToggle(tag)}
                  />
                  {channelLabel(tag, t)}
                </label>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
